import React from 'react';
import cx from 'classnames';

import { wordpressImgToImgixURL } from 'Utilities';
import { LANDING_PAGE_EVENTS, LANDING_PAGE_SECTIONS_NAMES } from 'Utilities/analytics';

import { Content } from 'LegacySBCRA/Content/Content';

import ResponsiveImage from '../../../../../components/misc/ResponsiveImage/ResponsiveImage';
import { usePageRefEvents } from '../../../common/Hooks/usePageRefEvents';
import { SEOContentBlock } from '../SEOContentBlocks/SEOContentBlock';

import styles from './SEOSection.scss';

type Props = {
  acf: Record<string, string>;
  isTrimmedContentVersion?: boolean;
}

const Image = (prop: { alt: string; src: string }) => (<div className={styles.imageContainer}>
  <ResponsiveImage
    isStaticScreenSize
    imgProps={{
      alt: prop?.alt,
      height: 440,
      loading: 'lazy',
      width: 440,
    }}
    imgSrc={prop?.src ? wordpressImgToImgixURL(prop?.src) : null}
  />
</div>);

export const SEOSection = ({ acf, isTrimmedContentVersion = false }: Props) => {
  const titles = {
    completeSaleTitle: acf?.section_five_title,
    getBestPriceTitle: acf?.section_four_title,
    valueTitle: acf?.section_three_title,
  } as const;

  const eventLabels = [
    titles.valueTitle,
    titles.getBestPriceTitle,
    titles.completeSaleTitle,
  ];

  const onSectionLoad = (label: string) => LANDING_PAGE_EVENTS.SECTION_LOAD({
    label,
    name: LANDING_PAGE_SECTIONS_NAMES.HOW_IT_WORKS,
  });

  const {
    $wrapper,
    entryMap: eventMap,
    setRef: setSEOSectionRef,
  } = usePageRefEvents({
    eventLabels,
    onRefTrigger: (label?: string) => onSectionLoad(label || ''),
  });

  const setSEORef:<T extends Element>(node: T, index: number) => void = (ref, index) => {
    const entry = eventMap.get(eventLabels[index]);
    if (!entry?.isIntersecting) {
      setSEOSectionRef(ref, index);
    }
  };

  const contentData = {
    bestPrice: {
      className: styles.getPrice,
      contentBody: acf?.section_four_body_area,
      imgSrc: <Image
        alt={acf?.section_four_image_area_alt}
        src={acf?.section_four_image_area}
      />,
      title: titles.getBestPriceTitle,
    } as const,
    completeSale: {
      className: styles.completeSale,
      contentBody: acf?.section_five_body_area,
      imgSrc: <Image
        alt={acf?.section_five_image_area_alt}
        src={acf?.section_five_image_area}
      />,
      title: titles.completeSaleTitle,
    } as const,
    valueCar: {
      className: styles.topCard,
      contentBody: acf?.section_three_body_area,
      imgSrc: <Image
        alt={acf?.section_three_image_area_alt}
        src={acf?.section_three_image_area}
      />,
      title: titles.valueTitle,
    } as const,
  };

  return (
    <Content className={styles.component} wrapper={{ className: cx(styles.componentWrapper) }}>
      <div ref={$wrapper}>
        <SEOContentBlock
          ref={(ref) => ref && setSEORef(ref, 0)}
          data-seo-content
          className={contentData.valueCar.className}
          contentBody={contentData.valueCar.contentBody}
          imgSrc={contentData.valueCar.imgSrc}
          title={contentData.valueCar.title}
        />

        <div className={cx(styles.bottomCards, {
          [styles.noLayout]: isTrimmedContentVersion,
        })}>
          <SEOContentBlock
            ref={(ref) => ref && setSEORef(ref, 1)}
            data-seo-content
            reverse
            className={contentData.bestPrice.className}
            contentBody={contentData.bestPrice.contentBody}
            imgSrc={contentData.bestPrice.imgSrc}
            title={contentData.bestPrice.title}
          />
          {!isTrimmedContentVersion && <SEOContentBlock
            ref={(ref) => ref && setSEORef(ref, 2)}
            data-seo-content
            className={contentData.completeSale.className}
            contentBody={contentData.completeSale.contentBody}
            imgSrc={contentData.completeSale.imgSrc}
            title={contentData.completeSale.title}
          />}
        </div>
      </div>
    </Content>
  );
};
